// useBoqSeedFromTemplate.js
//
// Wraps the boq.create_project_from_template() RPC. Refer to migration
// 20260529170000_boq_create_project_from_template.sql for what the function
// does on the server side (zones + 62 placeholder lines + bill_adjustments
// + clauses + baseline bill_version snapshot).
//
// Surface mirrors useBoqBillVersions: `seedFromTemplate(projectId, opts)`
// returns `{ success, summary?, error? }`. Callers are responsible for
// refreshing dependent hooks after a successful seed (project_items,
// bill_adjustments, project_clauses, bill_versions).

import { useCallback, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

export default function useBoqSeedFromTemplate() {
  const [submitting, setSubmitting] = useState(false);

  const seedFromTemplate = useCallback(
    async (projectId, { stage = 'sd', consultant } = {}) => {
      if (!projectId) {
        return { success: false, error: 'No BOQ project id — set up BOQ for this project first.' };
      }
      setSubmitting(true);
      try {
        const { data, error: rpcErr } = await supabase.schema('boq').rpc(
          'create_project_from_template',
          {
            p_project_id: projectId,
            p_stage: stage,
            // Only pass consultant if explicitly supplied; otherwise let the
            // function use its DEFAULT ('Architecture BRIO (in-house QS)').
            ...(consultant ? { p_consultant: consultant } : {}),
          }
        );
        if (rpcErr) throw rpcErr;
        return { success: true, summary: data };
      } catch (err) {
        console.error('[useBoqSeedFromTemplate] failed', err);
        return {
          success: false,
          error: formatSupabaseError(err, { action: 'seed', entity: 'project from template' }),
        };
      } finally {
        setSubmitting(false);
      }
    },
    []
  );

  return { seedFromTemplate, submitting };
}
